import React, { useState, useEffect } from 'react';
import { FileText, Sheet, Table, X, AlertTriangle, Eye, Download, RefreshCw } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useApi } from '../../hooks/useApi';

const COLORS = {
 primary: "#003087",  
 secondary: "#FFD100", 
 accent: "#DC3545"
};

const Alert = ({ message, type = 'error', onClose }) => (
 <div className={`rounded-lg p-3 mb-4 flex items-start justify-between ${
   type === 'error' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'
 }`}>
   <div className="flex items-start">
     <AlertTriangle className="h-4 w-4 mr-2 mt-0.5" />
     <p className="text-sm font-medium">{message}</p>
   </div>
   <button onClick={onClose} className="ml-4 text-gray-400 hover:text-gray-500">
     <X className="h-4 w-4" />
   </button>
 </div>
);

const LoadingSpinner = () => (
 <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#003087] border-t-transparent" />
);

const formatOptions = [
 { id: 'pdf', label: 'PDF', icon: FileText },
 { id: 'csv', label: 'CSV', icon: Table },
 { id: 'json', label: 'JSON', icon: Sheet }
];

const ReportsSection = ({ role = 'admin' }) => {
 const { get, post } = useApi();
 const [viewMode, setViewMode] = useState('generate');
 const [reportType, setReportType] = useState('institutional');
 const [dateRange, setDateRange] = useState([null, null]);
 const [selectedFormat, setSelectedFormat] = useState('pdf');
 const [selectedCounselor, setSelectedCounselor] = useState('');
 const [selectedClient, setSelectedClient] = useState('');
 const [pdfUrl, setPdfUrl] = useState(null);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState(null);
 const [counselors, setCounselors] = useState([]);
 const [clients, setClients] = useState([]);
 const [loadingUsers, setLoadingUsers] = useState(false);
 const [showPdfViewer, setShowPdfViewer] = useState(false);
 const [generatedReports, setGeneratedReports] = useState([]);

 useEffect(() => {
   const fetchUsers = async () => {
     setLoadingUsers(true);
     try {
       if (reportType === 'counselor') {
         const response = await get('/users', { params: { role: 'counselor', limit: 100 }});
         setCounselors(response.data.data || []);
       }
       if (reportType === 'client') {
         const response = await get('/users', { params: { role: 'student', limit: 100 }});
         setClients(response.data.data || []);
       }
     } catch (error) {
       setError('Failed to load users. Please try again.');
     } finally {
       setLoadingUsers(false);
     }
   };

   setSelectedCounselor('');
   setSelectedClient('');
   
   if (['counselor', 'client'].includes(reportType)) {
     fetchUsers();
   }
 }, [reportType, get]);

 useEffect(() => {
   if (showPdfViewer && pdfUrl) {
     const adobeDCView = new window.AdobeDC.View({
       clientId: process.env.REACT_APP_ADOBE_CLIENT_ID,
       divId: "adobe-dc-view"
     });
     adobeDCView.previewFile({
       content: { location: { url: pdfUrl } },
       metaData: { fileName: "Report.pdf" }
     });
   }
 }, [showPdfViewer, pdfUrl]);

 const handleGenerateReport = async () => {
   try {
     setLoading(true);
     setError(null);
     const [startDate, endDate] = dateRange;

     if (!startDate || !endDate) throw new Error('Please select a date range');
     if (endDate < startDate) throw new Error('End date must be after start date');
     if (reportType === 'counselor' && !selectedCounselor) throw new Error('Please select a counselor');
     if (reportType === 'client' && !selectedClient) throw new Error('Please select a client');

     const response = await post('/dashboard/reports', {
       startDate: startDate.toISOString(),
       endDate: endDate.toISOString(),
       format: selectedFormat,
       reportType,
       ...(reportType === 'counselor' && { counselorId: selectedCounselor }),
       ...(reportType === 'client' && { clientId: selectedClient })
     }, {
       responseType: selectedFormat === 'pdf' ? 'blob' : 'text',
       headers: {
         'Accept': selectedFormat === 'pdf' 
           ? 'application/pdf' 
           : selectedFormat === 'csv'
             ? 'text/csv'
             : 'application/json'
       }
     });

     if (selectedFormat === 'pdf') {
       const blob = new Blob([response.data], { type: 'application/pdf' });
       const url = window.URL.createObjectURL(blob);
       setPdfUrl(url);
       setShowPdfViewer(true);
     } else {
       const blob = new Blob(
         [response.data],
         { type: selectedFormat === 'csv' ? 'text/csv' : 'application/json' }
       );
       const url = window.URL.createObjectURL(blob);
       const link = document.createElement('a');
       link.href = url;
       const timestamp = new Date().toISOString().split('T')[0];
       link.download = `uzima-${reportType}-report-${timestamp}.${selectedFormat}`;
       document.body.appendChild(link);
       link.click();
       document.body.removeChild(link);
       window.URL.revokeObjectURL(url);
     }
   } catch (err) {
     setError(err?.response?.data?.message || err.message || 'Failed to generate report');
   } finally {
     setLoading(false);
   }
 };

 return (
   <div className="bg-white rounded-lg shadow-sm">
     <div className="border-b border-gray-100 p-4">
       <div className="flex justify-between items-center">
         <h2 className="text-lg font-medium text-gray-900 flex items-center">
           <FileText className="w-5 h-5 mr-2" style={{ color: COLORS.primary }} />
           Generate Reports
         </h2>
         <div className="flex gap-2">
           <button
             onClick={() => setViewMode('generate')}
             className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
               viewMode === 'generate' 
                 ? 'bg-[#003087] text-white' 
                 : 'text-gray-600 hover:bg-gray-100'
             }`}
           >
             New Report
           </button>
           <button
             onClick={() => setViewMode('view')}
             className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
               viewMode === 'view' 
                 ? 'bg-[#003087] text-white' 
                 : 'text-gray-600 hover:bg-gray-100'
             }`}
           >
             History
           </button>
         </div>
       </div>
     </div>

     <div className="p-4">
       {error && <Alert message={error} onClose={() => setError(null)} />}

       {viewMode === 'generate' ? (
         <div className="space-y-4">
           <div className="grid grid-cols-1 gap-4">
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
               <select
                 value={reportType}
                 onChange={(e) => setReportType(e.target.value)}
                 className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-[#003087]"
               >
                 {role === 'admin' && <option value="institutional">Institutional Report</option>}
                 <option value="counselor">Counselor Performance</option>
                 {/* <option value="client">Client Progress</option> */}
               </select>
             </div>

             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
               <div className="grid grid-cols-2 gap-3">
                 <DatePicker
                   selected={dateRange[0]}
                   onChange={(date) => setDateRange([date, dateRange[1]])}
                   className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                   placeholderText="Start date"
                   maxDate={new Date()}
                 />
                 <DatePicker
                   selected={dateRange[1]}
                   onChange={(date) => setDateRange([dateRange[0], date])}
                   className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                   placeholderText="End date"
                   minDate={dateRange[0]}
                   maxDate={new Date()}
                 />
               </div>
             </div>
           </div>

           {reportType === 'counselor' && (
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Select Counselor</label>
               {loadingUsers ? (
                 <div className="flex items-center space-x-2 text-gray-500">
                   <LoadingSpinner />
                   <span className="text-sm">Loading counselors...</span>
                 </div>
               ) : counselors.length > 0 ? (
                 <select
                   className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-[#003087]"
                   value={selectedCounselor}
                   onChange={(e) => setSelectedCounselor(e.target.value)}
                 >
                   <option value="">Select a counselor</option>
                   {counselors.map(counselor => (
                     <option key={counselor._id} value={counselor._id}>
                       {counselor.fullName || counselor.email}
                     </option>
                   ))}
                 </select>
               ) : (
                 <p className="text-sm text-red-500">No counselors available</p>
               )}
             </div>
           )}

           {reportType === 'client' && (
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Select Client</label>
               {loadingUsers ? (
                 <div className="flex items-center space-x-2 text-gray-500">
                   <LoadingSpinner />
                   <span className="text-sm">Loading clients...</span>
                 </div>
               ) : clients.length > 0 ? (
                 <select
                   className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-[#003087]"
                   value={selectedClient}
                   onChange={(e) => setSelectedClient(e.target.value)}
                 >
                   <option value="">Select a client</option>
                   {clients.map(client => (
                     <option key={client._id} value={client._id}>
                       {client.fullName || client.email}
                     </option>
                   ))}
                 </select>
               ) : (
                 <p className="text-sm text-red-500">No clients available</p>
               )}
             </div>
           )}

           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
             <div className="flex gap-3">
               {formatOptions.map(({ id, label, icon: Icon }) => (
                 <button
                   key={id}
                   onClick={() => setSelectedFormat(id)}
                   className={`flex items-center px-3 py-1.5 rounded-md transition-all ${
                     selectedFormat === id
                       ? 'bg-uzima-maroon-50 text-[#003087] border border-[#003087]'
                       : 'border border-gray-200 hover:border-gray-300'
                   }`}
                 >
                   <Icon className="w-4 h-4 mr-2" />
                   <span className="text-sm">{label}</span>
                 </button>
               ))}
             </div>
           </div>

           <div className="flex justify-end">
             {selectedFormat === 'pdf' && pdfUrl && (
               <button 
                 onClick={() => setShowPdfViewer(true)}
                 className="flex items-center px-3 py-1.5 mr-3 text-[#003087] hover:bg-uzima-maroon-50 rounded-md transition-colors"
               >
                 <Eye className="w-4 h-4 mr-2" />
                 <span className="text-sm">Preview</span>
               </button>
             )}
             <button
               onClick={handleGenerateReport}
               disabled={loading}
               className="flex items-center px-4 py-1.5 bg-[#003087] text-white rounded-md hover:bg-[#002670] transition-colors disabled:opacity-50 text-sm"
             >
               {loading ? (
                 <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
               ) : (
                 <Download className="w-4 h-4 mr-2" />
               )}
               Generate Report
             </button>
           </div>
         </div>
       ) : (
         <div className="space-y-3">
           {generatedReports.map(report => (
             <div key={report.id} className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50">
               <div className="flex items-center">
                 <FileText className="w-4 h-4 mr-2 text-[#003087]" />
                 <div>
                   <h3 className="text-sm font-medium text-gray-900">{report.name}</h3>
                   <p className="text-xs text-gray-500">Generated on {report.date}</p>
                 </div>
               </div>
               <div className="flex gap-2">
                 <button className="p-1.5 text-gray-500 hover:text-[#003087] rounded-md hover:bg-uzima-maroon-50">
                   <Eye className="w-4 h-4" />
                 </button>
                 <button className="p-1.5 text-gray-500 hover:text-[#003087] rounded-md hover:bg-uzima-maroon-50">
                   <Download className="w-4 h-4" />
                 </button>
               </div>
             </div>
           ))}
         </div>
       )}

{showPdfViewer && pdfUrl && (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
           <div className="bg-white w-4/5 h-4/5 rounded-lg flex flex-col">
             <div className="p-4 border-b border-gray-200 flex justify-between items-center">
               <h3 className="text-lg font-medium">Report Preview</h3>
               <button 
                 onClick={() => setShowPdfViewer(false)}
                 className="text-gray-500 hover:text-gray-700"
               >
                 <X className="w-5 h-5" />
               </button>
             </div>
             <div className="flex-1 p-4">
               <div id="adobe-dc-view" className="h-full w-full"></div>
             </div>
             <div className="p-4 border-t border-gray-200 flex justify-end gap-4">
               <button
                 onClick={() => setShowPdfViewer(false)}
                 className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
               >
                 Close
               </button>
               <button
                 onClick={() => {
                   const link = document.createElement('a');
                   link.href = pdfUrl;
                   link.download = `uzima-report-${new Date().toISOString().split('T')[0]}.pdf`;
                   document.body.appendChild(link);
                   link.click();
                   document.body.removeChild(link);
                 }}
                 className="px-4 py-2 bg-[#003087] text-white rounded-lg hover:bg-[#002670]"
               >
                 Download PDF
               </button>
             </div>
           </div>
         </div>
       )}
     </div>
   </div>
 );
};

export default ReportsSection;